import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface Exchange {
  exchCode: string;
  exchange: string;
  region: string | null;
  ticker: string;
  yahooSymbol: string;
  currency: string;
}

interface LookupResult {
  name: string;
  securityType: string;
  isin: string | null;
  wkn: string | null;
  exchanges: Exchange[];
}

interface AddAssetDialogProps {
  assetType: string;
  onClose: () => void;
  onCreated: () => void;
}

const INPUT: React.CSSProperties = {
  width: '100%', padding: '10px 14px', backgroundColor: '#1E2330',
  border: '1px solid #2A3040', borderRadius: '8px', color: '#E8EAED',
  fontSize: '0.85rem', outline: 'none',
};
const LABEL: React.CSSProperties = {
  display: 'block', fontSize: '0.65rem', fontWeight: 600, color: '#7B8399',
  marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em',
};

export default function AddAssetDialog({ assetType, onClose, onCreated }: AddAssetDialogProps) {
  const [step, setStep] = useState<'search' | 'select' | 'saving' | 'done'>('search');
  const [isin, setIsin] = useState('');
  const [wkn, setWkn] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<LookupResult | null>(null);
  const [selectedExchange, setSelectedExchange] = useState<Exchange | null>(null);
  const [fetchResult, setFetchResult] = useState<{ success: boolean; candles: number; message: string } | null>(null);

  const handleSearch = async () => {
    if (!isin && !wkn) return;
    setSearching(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/market/assets/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isin: isin || undefined,
          wkn: wkn || undefined,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          setError(`Asset existiert bereits: ${data.existing?.symbol} — ${data.existing?.name}`);
        } else {
          setError(data.error || 'Fehler bei der Suche');
        }
        setSearching(false);
        return;
      }

      setResult(data);
      setStep('select');
    } catch (err) {
      setError('Verbindungsfehler');
    }
    setSearching(false);
  };

  const handleSave = async () => {
    if (!selectedExchange || !result) return;
    setStep('saving');

    try {
      const res = await fetch(`${API_BASE}/market/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: selectedExchange.yahooSymbol,
          name: result.name,
          type: assetType,
          currency: selectedExchange.currency,
          exchange: selectedExchange.exchange,
          region: selectedExchange.region,
          isin: result.isin || isin || null,
          wkn: result.wkn || wkn || null,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || data.error || 'Fehler beim Speichern');
        setStep('select');
        return;
      }

      setFetchResult(data.history_fetch);
      setStep('done');
    } catch (err) {
      setError('Verbindungsfehler');
      setStep('select');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '560px', backgroundColor: '#151921', border: '1px solid #1E2330',
        borderRadius: '16px', padding: '28px', maxHeight: '80vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800 }}>Asset hinzufügen</h2>
          <button onClick={onClose} style={{
            border: 'none', background: 'none', color: '#4A5068', fontSize: '1.2rem', cursor: 'pointer',
          }}><i className="fa-solid fa-xmark"></i></button>
        </div>

        {/* Step 1: Search */}
        {step === 'search' && (
          <div>
            <p style={{ color: '#7B8399', fontSize: '0.8rem', marginBottom: '16px' }}>
              ISIN oder WKN eingeben — wir suchen die Daten automatisch über OpenFIGI.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={LABEL}>ISIN</label>
                <input
                  style={INPUT}
                  value={isin}
                  onChange={e => setIsin(e.target.value.toUpperCase())}
                  placeholder="z.B. DE0007030009"
                  maxLength={12}
                  autoFocus
                />
              </div>
              <div>
                <label style={LABEL}>WKN</label>
                <input
                  style={INPUT}
                  value={wkn}
                  onChange={e => setWkn(e.target.value.toUpperCase())}
                  placeholder="z.B. 703000"
                  maxLength={6}
                />
              </div>
            </div>

            {error && (
              <div style={{
                padding: '10px 14px', backgroundColor: '#FF475715', border: '1px solid #FF475740',
                borderRadius: '8px', color: '#FF4757', fontSize: '0.75rem', marginBottom: '12px',
              }}>
                {error}
              </div>
            )}

            <button
              onClick={handleSearch}
              disabled={searching || (!isin && !wkn)}
              style={{
                width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
                backgroundColor: '#00D97E', color: '#0B0E14', fontSize: '0.85rem',
                fontWeight: 700, cursor: 'pointer', opacity: searching || (!isin && !wkn) ? 0.5 : 1,
              }}
            >
              {searching ? 'Suche läuft...' : 'Suchen'}
            </button>
          </div>
        )}

        {/* Step 2: Select exchange */}
        {step === 'select' && result && (
          <div>
            <div style={{
              padding: '14px', backgroundColor: '#1E2330', borderRadius: '10px', marginBottom: '16px',
            }}>
              <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>{result.name}</div>
              <div style={{ display: 'flex', gap: '8px', fontSize: '0.7rem', color: '#7B8399' }}>
                {result.isin && <span>ISIN: <span style={{ color: '#E8EAED', fontFamily: 'monospace' }}>{result.isin}</span></span>}
                {result.wkn && <span>WKN: <span style={{ color: '#E8EAED', fontFamily: 'monospace' }}>{result.wkn}</span></span>}
                <span>Typ: {result.securityType}</span>
              </div>
            </div>

            <label style={{ ...LABEL, marginBottom: '8px' }}>Handelsplatz wählen</label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
              {result.exchanges.map(ex => (
                <button
                  key={ex.exchCode}
                  onClick={() => setSelectedExchange(ex)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 14px', borderRadius: '8px',
                    border: selectedExchange?.exchCode === ex.exchCode ? '1px solid #00D97E' : '1px solid #2A3040',
                    backgroundColor: selectedExchange?.exchCode === ex.exchCode ? '#00D97E10' : '#1E2330',
                    color: '#E8EAED', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>{ex.exchange}</span>
                    <span style={{ color: '#4A5068', fontSize: '0.7rem', marginLeft: '8px' }}>{ex.exchCode}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{
                      padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                      backgroundColor: '#1E2330', color: '#7B8399', fontFamily: 'monospace',
                    }}>
                      {ex.yahooSymbol}
                    </span>
                    <span style={{
                      padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 700,
                      backgroundColor: '#3B82F620', color: '#3B82F6',
                    }}>
                      {ex.currency}
                    </span>
                    {ex.region && (
                      <span style={{ fontSize: '0.65rem', color: '#4A5068' }}>{ex.region}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {error && (
              <div style={{
                padding: '10px 14px', backgroundColor: '#FF475715', border: '1px solid #FF475740',
                borderRadius: '8px', color: '#FF4757', fontSize: '0.75rem', marginBottom: '12px',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => { setStep('search'); setResult(null); setSelectedExchange(null); setError(''); }}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: '1px solid #2A3040', backgroundColor: 'transparent',
                  color: '#7B8399', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                ← Zurück
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedExchange}
                style={{
                  flex: 2, padding: '12px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#00D97E', color: '#0B0E14', fontSize: '0.85rem',
                  fontWeight: 700, cursor: 'pointer', opacity: selectedExchange ? 1 : 0.4,
                }}
              >
                Speichern
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Saving */}
        {step === 'saving' && (
          <div style={{ textAlign: 'center', padding: '30px', color: '#7B8399' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⏳</div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>Asset wird gespeichert...</div>
            <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>Historische Daten werden geladen — das kann einen Moment dauern.</div>
          </div>
        )}

        {/* Step 4: Done */}
        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: fetchResult?.success ? '#00D97E' : '#FBBF24' }}>
              <i className={fetchResult?.success ? 'fa-solid fa-circle-check' : 'fa-solid fa-triangle-exclamation'}></i>
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>
              {result?.name}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#7B8399', marginBottom: '16px' }}>
              {selectedExchange?.yahooSymbol} · {selectedExchange?.exchange} · {selectedExchange?.currency}
            </div>

            <div style={{
              padding: '14px', borderRadius: '10px', marginBottom: '20px',
              backgroundColor: fetchResult?.success ? '#00D97E10' : '#FBBF2410',
              border: `1px solid ${fetchResult?.success ? '#00D97E40' : '#FBBF2440'}`,
            }}>
              {fetchResult?.success ? (
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#00D97E' }}>
                    {fetchResult.candles.toLocaleString('de-DE')} historische Kurse geladen
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#7B8399', marginTop: '4px' }}>
                    Asset ist bereit — Daten ab sofort in den Charts verfügbar.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#FBBF24' }}>
                    Asset gespeichert — keine historischen Daten gefunden
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#7B8399', marginTop: '4px' }}>
                    {fetchResult?.message || 'Yahoo Finance hat keine Daten für dieses Symbol.'}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={onCreated}
              style={{
                width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
                backgroundColor: '#00D97E', color: '#0B0E14', fontSize: '0.85rem',
                fontWeight: 700, cursor: 'pointer',
              }}
            >
              Schließen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
